import React, { useState, useEffect } from "react";
import {
  makeStyles,
  shorthands,
  Toaster,
  Field,
  Button,
} from "@fluentui/react-components";
import { Category, Report } from "../../types/reportTypes";
import reportConfig from "../../shared/reportConfig.json";
import apiServices from "../../service";
import { useToastMessage } from "../../components/ToastMessage/ToastMessage";
import OverlayLoader from "../../components/OverlayLoader";
import { transformReportConfig } from "../../utils/reportTransform";
import ReportSelector from "./components/ReportSelector";
import ReportForm from "./components/ReportForm";
import "./style.css";

const useStyles = makeStyles({
  root: { ...shorthands.padding("16px") },
  container: { display: "flex", gap: "10px" },
  reportSelector: { display: "flex", flexDirection: "column" },
  formField: { display: "flex", alignItems: "baseline", gap: "10px" },
  error: {
    color: "#D13438",
    fontSize: "12px",
    ...shorthands.margin("4px", 0, 0, 0),
  },
});

function DynamicReport() {
  const styles = useStyles();
  const { showToast, toasterId } = useToastMessage();
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [error, setError] = useState<string | null>(null);

  const getReportList = () => {
    setIsLoading(true);
    setError(null);

    // For now, use the local reportConfig
    // In production, you might want to fetch from API
    // try {
    //   const transformedCategories = transformReportConfig(reportConfig);
    //   setCategories(transformedCategories);
    //   setIsLoading(false);
    // } catch (e) {
    //   console.error("Error transforming report config:", e);
    //   setError("An error occurred while loading the report configuration");
    //   setIsLoading(false);
    // }

    // Uncomment below for API-based loading
    apiServices.getReport
      .getList()
      .then((res: any) => {
        if (!res.data.isError) {
          if (res.data) {
            const transformedCategories = transformReportConfig(res.data);
            setCategories(transformedCategories);
          }
        } else {
          setError("Failed to load report list");
        }
        setIsLoading(false);
      })
      .catch((e: Error) => {
        console.error("Error fetching report list:", e);
        setError("An error occurred while fetching the report list");
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getReportList();
  }, []);

  const handleSelectReport = (report: Report) => {
    setSelectedReport({
      ...report,
      parameters: report.Metadata?.parameters || [],
    });
  };

  const handleReportGenerated = () => {
    setSelectedReport(null);
  };

  return (
    <>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />
      <div
        className={styles.root}
        style={{ display: "flex", gap: 10, alignItems: "end" }}
      >
        <OverlayLoader isLoading={isLoading} />
        {error && <div style={{ color: "red" }}>{error}</div>}

        <div className={styles.container}>
          <div className={styles.formField}>
            <Field label="Select a Report">
              <ReportSelector
                categories={categories}
                selectedReport={selectedReport}
                onSelectReport={handleSelectReport}
              />
            </Field>
          </div>

          {selectedReport && (
            <ReportForm
              selectedReport={selectedReport}
              onReportGenerated={handleReportGenerated}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              showToast={(title, message, type) =>
                showToast(
                  title,
                  message,
                  type as "success" | "error" | "warning"
                )
              }
            />
          )}
        </div>
        {selectedReport && (
          <Button
            appearance="secondary"
            onClick={handleReportGenerated}
            className="asc-button-secondary"
          >
            Clear
          </Button>
        )}
      </div>
    </>
  );
}

export default DynamicReport;
